import { type Color, type Group, PointLight } from 'three';

/** Brightness falls in this many steps rather than smoothly. */
const LIGHT_STEPS = 4;
/** With flashes turned down a light keeps this much of its strength, spread over this much more time. */
export const SOFT_PEAK = 0.2;
export const SOFT_LIFE = 2.5;

interface LightSlot {
  readonly light: PointLight;
  age: number;
  life: number;
  peak: number;
  /** Explosions outrank muzzle flashes when the pool is busy. */
  strong: boolean;
}

/**
 * A fixed pool of point lights for muzzle flashes and explosions. They stay in the scene
 * and visible, idling at zero intensity: the light count - and therefore every compiled
 * shader - never changes mid-mission.
 */
export class LightPool {
  /**
   * Photosensitivity: lights swell and fade as a smooth bump instead of snapping to full strength,
   * so no frame is much brighter than the one before it.
   */
  soft = false;
  private readonly slots: LightSlot[] = [];

  constructor(root: Group, color: Color, count: number) {
    for (let i = 0; i < count; i++) {
      const light = new PointLight(color, 0, 8, 1.6);
      root.add(light);
      this.slots.push({ light, age: 0, life: 0, peak: 0, strong: false });
    }
  }

  /** Borrow a light. Weak requests (muzzle flashes) never evict a strong one (explosions). */
  flash(x: number, y: number, z: number, color: Color, peak: number, distance: number, life: number, strong: boolean): void {
    let pick: LightSlot | null = null;
    let best = -1;
    for (const slot of this.slots) {
      const idle = slot.age >= slot.life;
      if (!idle && slot.strong && !strong) continue;
      const score = idle ? 3 : (slot.strong ? 0 : 1) + slot.age / slot.life;
      if (score > best) {
        best = score;
        pick = slot;
      }
    }
    if (!pick) return;
    pick.age = 0;
    pick.life = this.soft ? life * SOFT_LIFE : life;
    pick.peak = this.soft ? peak * SOFT_PEAK : peak;
    pick.strong = strong;
    pick.light.color.copy(color);
    pick.light.distance = distance;
    pick.light.position.set(x, y, z);
  }

  step(dt: number): void {
    for (const slot of this.slots) {
      if (slot.age >= slot.life) {
        slot.light.intensity = 0;
        continue;
      }
      // Set before ageing, so even a 60 ms flash is lit for at least one rendered frame.
      slot.light.intensity = this.soft
        ? slot.peak * Math.sin((Math.PI * (slot.age + dt * 0.5)) / (slot.life + dt))
        : (slot.peak * Math.ceil((1 - slot.age / slot.life) * LIGHT_STEPS)) / LIGHT_STEPS;
      slot.age += dt;
    }
  }

  dispose(): void {
    for (const slot of this.slots) slot.light.dispose();
  }
}
